import { Base85 } from "./Base85";
import { FormatUtils } from "./FormatUtils";
import { HaxCompressor } from "./HaxCompressor";
import { HaxRNG } from "./HaxRNG";
import { ObjectUtils } from "./ObjectUtils";

declare let process: any;

export namespace Utils {
	export const Object = ObjectUtils;
	export const Format = FormatUtils;

	/** Generate a random integer between min and max(inclusive) */
	export function randomInt(min: number, max: number) {
		return Math.floor(Math.random() * (max - min + 1) + min);
	}

	/** Generate a random integer between min and max(exclusive) */
	export function rangeInt(min: number, max: number): number {
		min = (min) | 0;
		max = (max) | 0;
		return ((Math.random() * (max - min)) + min) | 0;
	}

	/** Generate a random float between min and max(exclusive) */
	export function rangeFloat(min: number, max: number): number {
		min = +(min);
		max = +(max);
		return +((Math.random() * (max - min)) + min);
	}

	export function pickArray<T>(array: T[]): T | undefined {
		if (array.length <= 0) {
			return undefined;
		}
		return array[Utils.rangeInt(0, array.length)];
	}

	export function minMax(value: number, min = 0, max = 1) {
		if (min >= max) { throw new Error("[minMax] min >= max"); }
		return (value > max) ? max : ((value < min) ? min : value);
	}

	/** Converts number from one range to another */
	export function remapRange(value: number, sourceMin = 0, sourceMax = 1, targetMin = 0, targetMax = 1) {
		if (sourceMin >= sourceMax) { throw new Error("[remapRange] sourceMin >= sourceMax"); }
		if (value > sourceMax) { value = sourceMax; }
		if (value < sourceMin) { value = sourceMin; }

		return (value - sourceMin) * (targetMax - targetMin) / (sourceMax - sourceMin) + targetMin;
	}

	export function sortedIndex(array: number[], value: number) {
		let low = 0;
		let high = array.length;

		while (low < high) {
			let mid = low + high >>> 1;
			if (array[mid] < value) {
				low = mid + 1;
			} else {
				high = mid;
			}
		}
		return low;
	}

	export function sortedInsert(array: number[], value: number) {
		let index = Utils.sortedIndex(array, value);
		array.splice(index, 0, value);
	}

	export function sortedIndexComparator<T>(array: T[], value: T, comparator: (a: T, b: T) => number) {
		let min = 0;
		let max = array.length;
		let index = (min + max) >>> 1;
		while (max > min) {
			if (comparator(value, array[index]) < 0) { max = index; } else { min = index + 1; }
			index = (min + max) >>> 1;
		}
		return index;
	}

	export function sortedInsertComparator<T>(array: T[], value: T, comparator: (a: T, b: T) => number) {
		let index = Utils.sortedIndexComparator(array, value, comparator);
		array.splice(index, 0, value);
	}

	export async function timeout(delay: number): Promise<number> {
		const lastTime = Date.now();
		return new Promise<number>(resolve => {
			setTimeout((args, ms) => {
				resolve(Date.now() - lastTime);
			}, delay);
		});
	}

	export function internalNextTick(cb: () => void) {
		if ((typeof process === 'object') && process && (typeof process.nextTick === 'function')) {
			return process.nextTick(cb);
		}
		if (typeof queueMicrotask === "function") { return queueMicrotask(cb); }
		if (typeof setImmediate === 'function') { return setImmediate(cb); }
		return setTimeout(cb, 0);
	}

	export function internalImmediate(cb: () => void) {
		if (typeof setImmediate === 'function') { return setImmediate(cb); }
		return setTimeout(cb, 0);
	}

	export async function immediate<T>(value?: T): Promise<T> {
		return new Promise<T>(resolve => {
			Utils.internalImmediate(() => resolve(value as T));
		});
	}

	export async function nextTick<T>(value?: T): Promise<T> {
		return new Promise<T>(resolve => {
			Utils.internalNextTick(() => resolve(value as T));
		});
	}

	export function strReplaceMulti(str: string, replace: { [K: string]: string }) {
		let out = "";

		let i = 0;
		while (i < str.length) {
			let replaced = false;
			for (const find in replace) {
				if (replace.hasOwnProperty(find)) {
					const target = replace[find];
					if (str.startsWith(find, i)) {
						out += target;
						i += find.length;
						replaced = true;
						break;
					}
				}
			}
			if (!replaced) {
				out += str[i];
				i++;
			}
		}
		return out;
	}

	let hidIndex = 0;
	export function generateHID() {
		const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
		let text = "";
		const time = new Date().valueOf();
		for (let i = 0; i < 8; i++) {
			text += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		for (let i = 0; i < 2; i++) {
			text += chars.charAt(Math.floor(i > 0 ? hidIndex / Math.pow(chars.length, i) : hidIndex) % chars.length);
		}
		hidIndex++;
		for (let i = 0; i < 6; i++) {
			text += chars.charAt(Math.floor(i > 0 ? time / Math.pow(chars.length, i) : time) % chars.length);
		}
		return text;
	}

	let huuidIndex = 0;
	let huuidRNG = new HaxRNG(Date.now());
	export function generateHUUID() {
		const time = Math.floor(Date.now() / 1000);
		let text = HaxCompressor.compressNumber(time, 6);

		text += HaxCompressor.compressNumber(huuidIndex, 3);
		for (let i = 0; i < 4; i++) {
			text += HaxCompressor.chars.charAt(Math.floor(Math.random() * HaxCompressor.charsLength));
		}
		for (let i = 0; i < 3; i++) {
			text += huuidRNG.nextHChar();
		}
		huuidIndex++;
		return text;
	}

	export function generateHUUID24() {
		const time = Math.floor(Date.now() / 1000);
		let text = HaxCompressor.compressNumber(time, 8);

		text += HaxCompressor.compressNumber(huuidIndex, 3);
		for (let i = 0; i < 6; i++) {
			text += HaxCompressor.chars.charAt(Math.floor(Math.random() * HaxCompressor.charsLength));
		}
		for (let i = 0; i < 7; i++) {
			text += huuidRNG.nextHChar();
		}
		huuidIndex++;
		return text;
	}

	export const imul = Math.imul != undefined ? Math.imul : (a: number, b: number) => {
		let result = (a & 0x003fffff) * b;
		if (a & 0xffc00000) result += ((a & 0xffc00000) * b) | 0;
		return result | 0;
	};

	// cyrb53 (public domain)
	export function hashString(str: string) {
		let h1 = 0xdeadbeef;
		let h2 = 0x41c6ce57;
		for (let i = 0, ch; i < str.length; i++) {
			ch = str.charCodeAt(i);
			h1 = Utils.imul(h1 ^ ch, 2654435761);
			h2 = Utils.imul(h2 ^ ch, 1597334677);
		}
		h1 = Utils.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Utils.imul(h2 ^ (h2 >>> 13), 3266489909);
		h2 = Utils.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Utils.imul(h1 ^ (h1 >>> 13), 3266489909);
		return Base85.default.intToString(h2 >>> 0) + Base85.default.intToString(h1 >>> 0);
	}

	export function equals(a: any, b: any, keyIgnore?: (key: string) => boolean) {
		if (a === b) {
			return true;
		}

		if (Array.isArray(a) && Array.isArray(b)) {
			if (a.length !== b.length) { return false; }

			for (let i = 0; i < a.length; i++) {
				if (!Utils.equals(a[i], b[i], keyIgnore)) { return false; }
			}

			return true;
		}

		if (!(typeof (a) === "object") || !(typeof (b) === "object") || a === null || b === null) {
			return false;
		}

		if (a.constructor !== b.constructor) { return false; }

		for (const key in a) {
			if ((keyIgnore && keyIgnore(key))) { continue; }
			if (!a.hasOwnProperty(key)) { continue; }
			if (!b.hasOwnProperty(key)) { return false; }
			if (a[key] === b[key]) { continue; }
			if (typeof (a[key]) !== "object") { return false; }
			if (!Utils.equals(a[key], b[key], keyIgnore)) { return false; }
		}

		for (const key in b) {
			if ((keyIgnore && keyIgnore(key))) { continue; }
			if (b.hasOwnProperty(key) && !a.hasOwnProperty(key)) { return false; }
		}
		return true;
	}

	export function shuffle<T>(array: T[]) {
		let len = array.length;
		for (let i = 0; i < len; i++) {
			let j = (Math.random() * (i + 1)) | 0;
			if (i != j) {
				[array[i], array[j]] = [array[j], array[i]];
			}
		}
	}

	export function randomWeightedObject<T extends { weight: number }>(object: { [K: string]: T }) {
		let totalWeight = 0;
		for (let name in object) {
			if (object.hasOwnProperty(name)) {
				let element = object[name];
				totalWeight += element.weight;
			}
		}

		let randomWeight = Math.random() * totalWeight;
		for (let name in object) {
			if (object.hasOwnProperty(name)) {
				let element = object[name];
				randomWeight -= element.weight;
				if (randomWeight <= 0) {
					return { key: name, value: element };
				}
			}
		}
		return undefined;
	}

	export function randomWeightedArray<T extends { weight: number }>(object: T[]) {
		let totalWeight = 0;
		for (let i = 0; i < object.length; i++) {
			const element = object[i];
			totalWeight += element.weight;
		}

		let randomWeight = Math.random() * totalWeight;
		for (let i = 0; i < object.length; i++) {
			const element = object[i];
			randomWeight -= element.weight;
			if (randomWeight <= 0) {
				return { key: i, value: element };
			}
		}
		return undefined;
	}

	export function getOrdinal(num: number) {
		let s = ["th", "st", "nd", "rd"];
		let v = num % 100;
		return num.toLocaleString("en-US") + (s[(v - 20) % 10] || s[v] || s[0]);
	}

	export function initArray<T>(size: number, value: ((i: number) => T) | T) {
		if (typeof value === "function") {
			const arr = [];
			arr.length = size;
			for (let i = 0; i < size; i++) {
				arr[i] = (value as ((i: number) => T))(i);
			}
			return arr;
		} else {
			const arr = [];
			arr.length = size;
			for (let i = 0; i < size; i++) {
				arr[i] = value;
			}
			return arr;
		}
	}

	export function initArray2<T>(sizeX: number, sizeY: number, value: ((x: number, y: number, i: number) => T)) {
		let size = sizeX * sizeY;
		const arr = [];
		arr.length = size;
		for (let y = 0; y < sizeY; y++) {
			for (let x = 0; x < sizeX; x++) {
				let i = y * sizeX + x;
				arr[i] = (value as ((x: number, y: number, i: number) => T))(x, y, i);
			}
		}
		return arr;
	}

	export function initArray3<T>(sizeX: number, sizeY: number, sizeZ: number, value: ((x: number, y: number, z: number, i: number) => T)) {
		let size = sizeX * sizeY * sizeZ;
		let sizeXY = sizeX * sizeY;
		const arr = [];
		arr.length = size;
		for (let z = 0; z < sizeZ; z++) {
			for (let y = 0; y < sizeY; y++) {
				for (let x = 0; x < sizeX; x++) {
					let i = z * sizeXY + y * sizeX + x;
					arr[i] = (value as ((x: number, y: number, z: number, i: number) => T))(x, y, z, i);
				}
			}
		}
		return arr;
	}

	export function normalizeNulls(data: any, inArray = false) {
		if (typeof (data) === "object") {
			// tslint:disable-next-line:forin
			for (const key in data) {
				let value = data[key];
				if (!inArray && value === null) {
					data[key] = undefined;
				} else if (typeof (value) === "object") {
					Utils.normalizeNulls(value, Array.isArray(value));
				}
			}
		}
	}
}
